/**
 * DiggingSystem - Block-based digging controller
 *
 * The new system treats digging as breaking chunky blocks (multiple terrain tiles)
 * with a short channel time, mimicking Minecraft-style mining. Each direction
 * (horizontal, down, up) owns its own channel so progress is preserved when you
 * hold a direction but quickly resets when you stop.
 */

export class DiggingSystem {
  constructor(tileSize = 12) {
    this.TILE_SIZE = tileSize;
    this.BLOCK_SCALE = 4; // each dig block spans 4x4 world tiles
    this.BLOCK_SIZE = this.TILE_SIZE * this.BLOCK_SCALE;

    // Time (seconds) required to chew through a block in each direction
    this.BASE_DIG_TIME = 0.55;
    this.VERTICAL_DIG_TIME = 0.65;
    this.UPWARD_DIG_TIME = 0.75;

    this.PROGRESS_DECAY = 0.5; // how fast inactive progress melts away
    this.GRACE_PERIOD_DURATION = 0.2; // protect player collision right after carving

    this.gracePeriod = 0;
    this.activeChannels = new Map(); // channel -> { key, progress, duration, centerX, centerY, active }
  }

  /**
   * Advance timers. If a channel was not worked this frame, bleed its progress down.
   */
  update(dt) {
    this.gracePeriod = Math.max(0, this.gracePeriod - dt);
    for (const [channel, entry] of this.activeChannels) {
      if (!entry.active) {
        entry.progress = Math.max(0, entry.progress - dt * this.PROGRESS_DECAY);
        if (entry.progress <= 0.001) {
          this.activeChannels.delete(channel);
          continue;
        }
      }
      entry.active = false;
    }
  }

  /**
   * Whether collision resolution should loosen up (prevents instant re-collision).
   */
  isInGracePeriod() {
    return this.gracePeriod > 0;
  }

  getBlockSize() {
    return this.BLOCK_SIZE;
  }

  resetChannel(channel) {
    this.activeChannels.delete(channel);
  }

  resetAll() {
    this.activeChannels.clear();
  }

  isChannelActive(channel) {
    return this.activeChannels.has(channel);
  }

  snapToBlockCenter(value) {
    const block = Math.floor(value / this.BLOCK_SIZE);
    return block * this.BLOCK_SIZE + this.BLOCK_SIZE * 0.5;
  }

  processDig(channel, rawX, rawY, dt, duration, carveBlock) {
    const centerX = this.snapToBlockCenter(rawX);
    const centerY = this.snapToBlockCenter(rawY);
    const key = `${channel}:${centerX}:${centerY}`;
    let entry = this.activeChannels.get(channel);

    if (!entry || entry.key !== key) {
      entry = { key, progress: 0, duration, centerX, centerY, active: false };
      this.activeChannels.set(channel, entry);
    } else {
      entry.centerX = centerX;
      entry.centerY = centerY;
      entry.key = key;
    }

    entry.active = true;
    entry.progress += dt;

    if (entry.progress >= entry.duration) {
      carveBlock(entry.centerX, entry.centerY, this.BLOCK_SIZE);
      this.activeChannels.delete(channel);
      this.gracePeriod = this.GRACE_PERIOD_DURATION;
      return true;
    }

    return false;
  }

  digHorizontal(dt, player, direction, isBlocked, carveBlock, isUnderground, allowSurfaceDig = false) {
    if ((!isUnderground && !allowSurfaceDig) || direction === 0) {
      this.resetChannel('horizontal');
      return false;
    }
    if (!isBlocked(direction)) {
      this.resetChannel('horizontal');
      return false;
    }

    const playerCenterX = player.x + player.w * 0.5;
    const playerCenterY = player.y + player.h * 0.5;
    const offset = player.w * 0.5 + this.BLOCK_SIZE * 0.55;
    const targetX = playerCenterX + direction * offset;
    const targetY = playerCenterY;

    return this.processDig('horizontal', targetX, targetY, dt, this.BASE_DIG_TIME, carveBlock);
  }

  digDown(dt, player, carveBlock) {
    const playerCenterX = player.x + player.w * 0.5;
    const playerFeet = player.y + player.h;
    const targetY = playerFeet + this.BLOCK_SIZE * 0.5;
    return this.processDig('down', playerCenterX, targetY, dt, this.VERTICAL_DIG_TIME, carveBlock);
  }

  digUp(dt, player, hasCeiling, carveBlock) {
    if (!hasCeiling) {
      this.resetChannel('up');
      return false;
    }
    const playerCenterX = player.x + player.w * 0.5;
    const headY = player.y;
    const targetY = headY - this.BLOCK_SIZE * 0.5;
    return this.processDig('up', playerCenterX, targetY, dt, this.UPWARD_DIG_TIME, carveBlock);
  }
}
